import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  Typography,
  Button,
  Box,
} from "@mui/material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";

/**
 * Shows a celebratory modal when the user contributes to a bonus.
 * @param {boolean} open
 * @param {Array<{ bonus_id, bonus_name, value, goal, current_total, remaining }>} contributions
 * @param {() => void} onClose
 */
export default function ContributionCelebrationModal({ open, contributions = [], onClose }) {
  const navigate = useNavigate();
  const first = contributions[0];

  useEffect(() => {
    if (!open || !first) return;
    const t = setTimeout(() => {
      // Simple "confetti" via DOM: add then remove a burst of divs
      const root = document.getElementById("confetti-root") || (() => {
        const el = document.createElement("div");
        el.id = "confetti-root";
        el.style.cssText = "position:fixed;inset:0;pointer-events:none;z-index:9999;";
        document.body.appendChild(el);
        return el;
      })();
      const colors = ["#5e72e4", "#2dce89", "#fb6340", "#11cdef", "#ffc107"];
      const angle = (i) => (i / 40) * 2 * Math.PI;
      for (let i = 0; i < 40; i++) {
        const d = document.createElement("div");
        const dist = 120 + Math.random() * 80;
        const x = Math.cos(angle(i)) * dist;
        const y = Math.sin(angle(i)) * dist;
        d.style.cssText = `
          position:absolute;left:50%;top:50%;width:8px;height:8px;
          background:${colors[i % colors.length]};
          border-radius:50%;
          animation:confetti-fall-${i} 1.5s ease-out forwards;
        `;
        const style = document.createElement("style");
        style.textContent = `@keyframes confetti-fall-${i}{to{transform:translate(${x}px,${y}px) scale(0);opacity:0;}}`;
        document.head.appendChild(style);
        root.appendChild(d);
        setTimeout(() => { d.remove(); style.remove(); }, 1600);
      }
      return () => clearTimeout(t);
    }, 300);
    return () => clearTimeout(t);
  }, [open, first]);

  if (!first) return null;

  const metricLabel = (first.bonus_name || "").toLowerCase().includes("conversion")
    ? "conversion"
    : (first.bonus_name || "").toLowerCase().includes("revenue")
      ? "revenue"
      : "contribution";
  const valueText = first.value === 1 && metricLabel === "conversion" ? "1 conversion" : `${first.value} ${metricLabel}(s)`;
  const remaining = first.remaining;
  const remainingText =
    remaining > 0
      ? `Only ${remaining} more ${metricLabel === "conversion" ? "conversions" : metricLabel === "revenue" ? "in revenue" : "to go"} to unlock the reward!`
      : "You've unlocked the reward!";

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogContent sx={{ textAlign: "center", py: 4 }}>
          <Box sx={{ color: "primary.main", mb: 2 }}>
            <EmojiEventsIcon sx={{ fontSize: 56 }} />
          </Box>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Great Job!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
            You contributed {valueText} to the {first.bonus_name}.
          </Typography>
          <Typography variant="body2" color="primary.main" fontWeight={600}>
            {remainingText}
          </Typography>
          <Box sx={{ mt: 3, display: "flex", gap: 1, justifyContent: "center" }}>
            <Button variant="outlined" onClick={onClose}>
              Close
            </Button>
            <Button variant="contained" onClick={() => { onClose(); navigate(`/bonuses/${first.bonus_id}`); }}>
              View Bonus
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
